import React, { useCallback, useId, useMemo, useState } from 'react';
import ReactSelect, {
    MultiValue,
    OnChangeValue,
    SelectInstance,
    SingleValue,
    StylesConfig,
    mergeStyles,
} from 'react-select';
import { useInputCommon } from '../../hooks';
import styles from './Select.module.scss';

export type SelectSize = 'sm' | 'md' | 'lg';

export type SelectOption = {
    value: string;
    label: string;
    disabled?: boolean;
    isDisabled?: boolean;
};

type CommonSelectProps = {
    label?: string;
    error?: boolean;
    errorText?: string;
    helperText?: string;
    options: SelectOption[];
    size?: SelectSize;
    isLoading?: boolean;
    isSearchable?: boolean;
    placeholder?: string;
    menuZIndex?: number;
    styles?: StylesConfig<SelectOption, boolean>;
};

type SingleSelectProps = CommonSelectProps & {
    isMulti?: false;
    value?: string;
    defaultValue?: string;
    onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'value' | 'onChange' | 'multiple'>;

type MultiSelectProps = CommonSelectProps & {
    isMulti: true;
    value?: string[];
    defaultValue?: string[];
    onChange?: (values: string[]) => void;
    id?: string;
    disabled?: boolean;
    required?: boolean;
    name?: string;
    className?: string;
    'data-testid'?: string;
    'aria-label'?: string;
    autoFocus?: boolean;
    tabIndex?: number;
};

export type SelectProps = SingleSelectProps | MultiSelectProps;

function createSyntheticChangeEvent(value: string, name?: string): React.ChangeEvent<HTMLSelectElement> {
    const target = { value, name: name ?? '' } as HTMLSelectElement;
    return {
        target,
        currentTarget: target,
    } as React.ChangeEvent<HTMLSelectElement>;
}

function optionFontSizeForSize(size: SelectSize): string {
    if (size === 'sm') return 'var(--font-size-sm)';
    if (size === 'lg') return 'var(--font-size-lg)';
    return 'var(--font-size-base)';
}

function buildStyles(size: SelectSize, hasError: boolean, menuZIndex: number): StylesConfig<SelectOption, boolean> {
    const optionFont = optionFontSizeForSize(size);
    return {
        control: (base, state) => ({
            ...base,
            borderColor: hasError ? 'var(--error-color)' : state.isFocused ? 'var(--border-purple)' : base.borderColor,
            boxShadow:
                state.isFocused && hasError
                    ? '0 0 0 2px rgba(204, 0, 0, 0.15)'
                    : state.isFocused
                      ? 'var(--input-focus-shadow)'
                      : base.boxShadow,
            borderRadius: 'var(--input-border-radius)',
            fontFamily: 'var(--font-family-primary)',
            '&:hover': {
                borderColor: hasError ? 'var(--error-color)' : 'var(--border-purple)',
            },
        }),
        menu: (base) => ({ ...base, zIndex: menuZIndex }),
        menuPortal: (base) => ({ ...base, zIndex: menuZIndex }),
        menuList: (base) => ({ ...base, padding: 0 }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? 'var(--primary-purple)'
                : state.isFocused
                  ? 'var(--primary-purple-lighter)'
                  : base.backgroundColor,
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            color: state.isSelected ? 'white' : state.isDisabled ? 'var(--gray-700)' : 'var(--black)',
            fontSize: optionFont,
        }),
        multiValue: (styles, { data }) => {
            return {
                ...styles,
                backgroundColor: 'var(--primary-purple-lighter)',
            };
        },
    };
}

type RsOption = { value: string; label: string; isDisabled?: boolean };

export const Select = React.forwardRef<SelectInstance<SelectOption, boolean>, SelectProps>((props, ref) => {
    const generatedId = useId().replace(/:/g, '');
    const {
        label,
        error,
        errorText,
        helperText,
        options,
        className,
        size = 'md',
        isLoading = false,
        isSearchable: isSearchableProp,
        placeholder,
        menuZIndex = 9999,
        styles: customStyles,
        disabled,
        required,
        name,
        id,
        isMulti: isMultiProp,
        value: valueProp,
        defaultValue,
        onChange,
        ...rest
    } = props;

    const resolvedInputId = id ?? `select-${generatedId}`;
    const isMulti = isMultiProp === true;

    const isSearchable = isSearchableProp ?? isMulti;

    const dataTestId =
        (props as MultiSelectProps & { dataTestId?: string }).dataTestId ??
        (props as { 'data-testid'?: string })['data-testid'];

    let restForReactSelect: Record<string, unknown>;
    let singleOnFocus: React.FocusEventHandler<HTMLInputElement> | undefined;
    let singleOnBlur: React.FocusEventHandler<HTMLInputElement> | undefined;

    if (isMulti) {
        const { 'data-testid': _dt, ...r } = rest as { 'data-testid'?: string } & Record<string, unknown>;
        restForReactSelect = r;
        singleOnFocus = undefined;
        singleOnBlur = undefined;
    } else {
        const {
            onFocus,
            onBlur,
            'data-testid': _dt,
            ...r
        } = rest as {
            onFocus?: React.FocusEventHandler<HTMLSelectElement>;
            onBlur?: React.FocusEventHandler<HTMLSelectElement>;
            'data-testid'?: string;
        } & Record<string, unknown>;
        restForReactSelect = r;
        singleOnFocus = onFocus as React.FocusEventHandler<HTMLInputElement> | undefined;
        singleOnBlur = onBlur as React.FocusEventHandler<HTMLInputElement> | undefined;
    }

    const [internalSingle, setInternalSingle] = useState(() =>
        !isMulti ? ((defaultValue as string | undefined) ?? '') : ''
    );
    const [internalMulti, setInternalMulti] = useState<string[]>(() =>
        isMulti ? ((defaultValue as string[] | undefined) ?? []) : []
    );

    const rsOptions: RsOption[] = useMemo(
        () =>
            options.map((o) => ({
                value: o.value,
                label: o.label,
                isDisabled: Boolean(o.isDisabled ?? o.disabled),
            })),
        [options]
    );

    const currentSingle = !isMulti ? (valueProp !== undefined ? (valueProp as string) : internalSingle) : '';
    const currentMulti: string[] = isMulti ? (valueProp !== undefined ? (valueProp as string[]) : internalMulti) : [];

    const selectedOption: SingleValue<RsOption> = useMemo(() => {
        if (isMulti) return null;
        return rsOptions.find((o) => o.value === currentSingle) ?? null;
    }, [isMulti, rsOptions, currentSingle]);

    const selectedOptions: MultiValue<RsOption> = useMemo(() => {
        if (!isMulti) return [];
        return currentMulti.map(
            (v) =>
                rsOptions.find((o) => o.value === v) ?? {
                    value: v,
                    label: v,
                }
        );
    }, [isMulti, rsOptions, currentMulti]);

    const isSingleControlled = !isMulti && valueProp !== undefined;
    const isMultiControlled = isMulti && valueProp !== undefined;

    const { labelElement, errorTextElement, helperTextElement, containerClasses } = useInputCommon({
        id: resolvedInputId,
        label,
        error: !!error,
        errorText,
        helperText,
        required: !!required,
        disabled: !!disabled,
        className,
        inputType: 'select',
        onFocus: singleOnFocus,
        onBlur: singleOnBlur,
    });

    const mergedStyles = useMemo(
        () => mergeStyles(buildStyles(size, !!error, menuZIndex), customStyles ?? {}),
        [size, error, menuZIndex, customStyles]
    );

    const handleChange = useCallback(
        (newValue: OnChangeValue<RsOption, boolean>) => {
            if (isMulti) {
                const vals = ((newValue as MultiValue<RsOption>) ?? []).map((o) => o.value);
                if (!isMultiControlled) {
                    setInternalMulti(vals);
                }
                (onChange as MultiSelectProps['onChange'])?.(vals);
            } else {
                const next = (newValue as SingleValue<RsOption>)?.value ?? '';
                if (!isSingleControlled) {
                    setInternalSingle(next);
                }
                (onChange as SingleSelectProps['onChange'])?.(createSyntheticChangeEvent(next, name));
            }
        },
        [isMulti, isMultiControlled, isSingleControlled, onChange, name]
    );

    const selectClassName = `${styles.select} ${styles[size]} ${className || ''}`.trim();

    return (
        <div className={containerClasses}>
            {labelElement}
            <div className={styles.selectField} data-testid={dataTestId}>
                <ReactSelect<RsOption, boolean>
                    {...(restForReactSelect as Record<string, unknown>)}
                    ref={ref}
                    inputId={resolvedInputId}
                    instanceId={resolvedInputId}
                    className={selectClassName}
                    classNamePrefix='react-select'
                    options={rsOptions}
                    value={isMulti ? selectedOptions : selectedOption}
                    onChange={handleChange}
                    isDisabled={isLoading || !!disabled}
                    isLoading={isLoading}
                    isSearchable={isSearchable}
                    isMulti={isMulti}
                    closeMenuOnSelect={isMulti ? false : true}
                    placeholder={placeholder}
                    styles={mergedStyles}
                    name={name}
                    required={required}
                    aria-invalid={error || undefined}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    {...(!isMulti ? { onFocus: singleOnFocus, onBlur: singleOnBlur } : {})}
                />
            </div>
            {errorTextElement}
            {helperTextElement}
        </div>
    );
});

Select.displayName = 'Select';
